import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  FaPlus,
  FaCheck,
  FaEdit,
  FaClock,
  FaStar,
  FaClipboardList,
  FaExclamationTriangle,
} from "react-icons/fa";

const MarksForm = ({
  handleSubmit,
  formData,
  setFormData,
  entries,
  disableAdd,
  isEditing,
  startEdit,
  isSubmitting
}) => {
  return (
    <Box>
      {/* ── Form ────────────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          background: "rgba(2,136,209,0.04)",
          border: "1px solid rgba(2,136,209,0.14)",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#0288D1",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            mb: 2,
          }}
        >
          {isEditing ? "✏️ Edit Entry" : "➕ New Exam Entry"}
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={1.5}>
            {/* Duration */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "#334155",
                  }}
                >
                  Exam Duration
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "#94a3b8",
                    fontStyle: "italic",
                  }}
                >
                  (hh:mm:ss)
                </Typography>
                {entries.length === 0 && (
                  <Tooltip title="Exam duration needs to be added" arrow>
                    <Box
                      sx={{ display: "flex", alignItems: "center", ml: 0.5 }}
                    >
                      <FaExclamationTriangle size={11} color="#f59e0b" />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
              <TextField
                type="text"
                value={formData.Time}
                onChange={(e) =>
                  setFormData({ ...formData, Time: e.target.value })
                }
                required
                size="small"
                fullWidth
                placeholder="e.g. 01:30:00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaClock size={13} color="#0288D1" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#0c1a2e",
                    borderRadius: 2,
                    background: "#fff",
                    "& fieldset": { borderColor: "rgba(2,136,209,0.25)" },
                    "&:hover fieldset": { borderColor: "#0288D1" },
                    "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                  },
                  "& input::placeholder": { color: "#94a3b8" },
                }}
              />
            </Box>

            {/* Objective Mark */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "#334155",
                  }}
                >
                  Objective Mark
                </Typography>
                {entries.length === 0 && (
                  <Tooltip title="Objective marks need to be added" arrow>
                    <Box
                      sx={{ display: "flex", alignItems: "center", ml: 0.5 }}
                    >
                      <FaExclamationTriangle size={11} color="#f59e0b" />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
              <TextField
                type="number"
                value={formData.marks}
                onChange={(e) =>
                  setFormData({ ...formData, marks: e.target.value })
                }
                required
                size="small"
                fullWidth
                placeholder="e.g. 40"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaStar size={13} color="#0288D1" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#0c1a2e",
                    borderRadius: 2,
                    background: "#fff",
                    "& fieldset": { borderColor: "rgba(2,136,209,0.25)" },
                    "&:hover fieldset": { borderColor: "#0288D1" },
                    "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                  },
                  "& input::placeholder": { color: "#94a3b8" },
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={disableAdd || isSubmitting}
              startIcon={
                isEditing ? <FaCheck size={11} /> : <FaPlus size={11} />
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.82rem",
                background: disableAdd
                  ? "#e2e8f0"
                  : "linear-gradient(135deg, #0288D1, #0ea5e9)",
                color: disableAdd ? "#94a3b8" : "#fff",
                boxShadow: disableAdd
                  ? "none"
                  : "0 4px 12px rgba(2,136,209,0.25)",
                cursor: disableAdd ? "not-allowed" : "pointer",
                "&:hover": {
                  background: disableAdd
                    ? "#e2e8f0"
                    : "linear-gradient(135deg, #0277bd, #0288D1)",
                },
                "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
              }}
            >
              {isEditing ? "Update Entry" : "Add Entry"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ── Entries List ─────────────────────────────────────────────────────── */}
      <Box>
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            mb: 1.5,
          }}
        >
          Your Entries ({entries.length})
        </Typography>

        {entries.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <FaClipboardList size={32} color="#bae6fd" />
            <Typography
              sx={{
                color: "#94a3b8",
                mt: 1.5,
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              No entries yet.
            </Typography>
            <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
              Fill the form above to add your first exam entry.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {entries.map((entry) => (
              <Paper
                key={`${entry.id}-${entry.Time}`}
                elevation={0}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "rgba(2,136,209,0.35)",
                    boxShadow: "0 2px 8px rgba(2,136,209,0.08)",
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    {/* Duration */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          background: "rgba(2,136,209,0.1)",
                          border: "1px solid rgba(2,136,209,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <FaClock size={12} color="#0288D1" />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: "#94a3b8",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            lineHeight: 1,
                          }}
                        >
                          Duration
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#0c1a2e",
                            fontFamily: "monospace",
                          }}
                        >
                          {entry.Time}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ borderColor: "#e2e8f0" }}
                    />

                    {/* Objective Mark */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          background: "rgba(124,58,237,0.1)",
                          border: "1px solid rgba(124,58,237,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <FaStar size={12} color="#7c3aed" />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: "#94a3b8",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            lineHeight: 1,
                          }}
                        >
                          Obj. Mark
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#0c1a2e",
                          }}
                        >
                          {entry.marks}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>

                  {/* Edit button */}
                  <Tooltip title="Edit entry" arrow>
                    <IconButton
                      size="small"
                      onClick={() => startEdit(entry)}
                      sx={{
                        color: "#0288D1",
                        background: "rgba(2,136,209,0.08)",
                        border: "1px solid rgba(2,136,209,0.2)",
                        borderRadius: 1.5,
                        "&:hover": { background: "rgba(2,136,209,0.16)" },
                      }}
                    >
                      <FaEdit size={13} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MarksForm;
